/**
 * Hero image parallax + copy fade.
 * Text fades out as it approaches the sticky header so it never stacks under the nav.
 */
(() => {
  const instances = new WeakMap();

  class HeroParallax {
    constructor(root) {
      this.root = root;
      this.layer = root.querySelector('[data-hero-parallax]');
      this.copy =
        root.querySelector('[data-hero-copy]') || root.querySelector('.hero__inner');
      // Accept 0–100 from the theme setting
      this.strength = (Number(root.dataset.scrollParallax) || 0) / 100;
      this.raf = null;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
      this.setup();
    }

    get reducedMotion() {
      return this.reducedMotionQuery.matches;
    }

    setup() {
      this.teardownScroll();

      const enableParallax = Boolean(this.layer) && this.strength > 0 && !this.reducedMotion;
      if (enableParallax) {
        this.layer.classList.add('hero__parallax--enabled');
        this.syncExtent();
      }

      if (enableParallax || this.copy) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        this.update();
      }
    }

    syncExtent() {
      // Extra image height so translate never exposes empty edges
      const extentPx = Math.ceil(window.innerHeight * this.strength * 1.25);
      this.layer.style.setProperty('--hero-scroll-parallax-extent', `${extentPx}px`);
    }

    teardownScroll() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      if (this.layer) {
        this.layer.classList.remove('hero__parallax--enabled');
        this.layer.style.removeProperty('--hero-scroll-parallax-extent');
        this.layer.style.removeProperty('--hero-scroll-parallax-offset');
      }
      this.resetCopyFade();
    }

    teardown() {
      this.teardownScroll();
      this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
    }

    onReducedMotionChange() {
      this.setup();
    }

    onResize() {
      if (this.layer?.classList.contains('hero__parallax--enabled')) {
        this.syncExtent();
      }
      this.update();
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    resetCopyFade() {
      if (!this.copy) return;
      this.copy.style.removeProperty('--hero-copy-opacity');
      this.copy.style.removeProperty('pointer-events');
    }

    updateCopyFade() {
      if (!this.copy) return;

      if (this.reducedMotion) {
        this.resetCopyFade();
        return;
      }

      const rootRect = this.root.getBoundingClientRect();

      // Skip once the hero is fully off-screen
      if (rootRect.bottom <= 0 || rootRect.top >= window.innerHeight) {
        this.copy.style.setProperty('--hero-copy-opacity', '0');
        this.copy.style.pointerEvents = 'none';
        return;
      }

      // Fade from how far the section has scrolled up — guarantees full
      // opacity at the rest position and a clean restore on scroll-back.
      const scrolled = Math.max(0, -rootRect.top);
      const fadeDistance = Math.max(96, Math.min(200, window.innerHeight * 0.22));
      const opacity = 1 - Math.min(1, scrolled / fadeDistance);

      this.copy.style.setProperty('--hero-copy-opacity', opacity.toFixed(3));
      this.copy.style.pointerEvents = opacity < 0.08 ? 'none' : '';
    }

    update() {
      if (this.layer && this.strength > 0 && !this.reducedMotion) {
        const rect = this.root.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const offset = window.scrollY * this.strength;
          this.layer.style.setProperty('--hero-scroll-parallax-offset', `${offset}px`);
        }
      }

      this.updateCopyFade();
    }

    destroy() {
      this.teardown();
    }
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement)) return;
    const existing = instances.get(root);
    if (existing) existing.destroy();
    instances.set(root, new HeroParallax(root));
  }

  function initAll() {
    document.querySelectorAll('[data-hero]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-hero]');
    if (root) initRoot(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-hero]');
    if (!root) return;
    const existing = instances.get(root);
    if (existing) {
      existing.destroy();
      instances.delete(root);
    }
  });
})();
